import React from 'react';

interface AnimatedLoadingProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  className?: string;
}

export const AnimatedLoading: React.FC<AnimatedLoadingProps> = ({
  size = 'md',
  text = 'Loading...',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  return (
    <div className={`flex flex-col items-center justify-center space-y-4 ${className}`}>
      {/* Spinning Ring */}
      <div className={`relative ${sizeClasses[size]}`}>
        <div className={`absolute inset-0 border-4 border-emerald-200 rounded-full animate-spin`}></div>
        <div className={`absolute inset-0 border-4 border-transparent border-t-emerald-500 rounded-full animate-spin`} 
             style={{ animationDuration: '0.8s' }}></div>
        <div className={`absolute inset-2 border-4 border-transparent border-b-emerald-600 rounded-full animate-spin`}
             style={{ animationDuration: '1.2s', animationDirection: 'reverse' }}></div>
      </div>
      
      {/* Loading Text */}
      {text && (
        <div className="text-slate-600 animate-pulse">
          <span className="loading-dots">{text}</span>
        </div>
      )}
    </div>
  );
};

// Pulse Dots Loading
export const PulseDots: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex space-x-2 ${className}`}>
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"
          style={{
            animationDelay: `${index * 0.2}s`,
            animationDuration: '1s'
          }}
        />
      ))}
    </div>
  );
};

// Bouncing Dots Loading
export const BouncingDots: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex space-x-1 ${className}`}>
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce"
          style={{
            animationDelay: `${index * 0.1}s`,
            animationDuration: '0.6s'
          }}
        />
      ))}
    </div>
  );
};

// Wave Loading
export const WaveLoading: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex space-x-1 ${className}`}>
      {[0, 1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className="w-1 bg-emerald-500 rounded-full animate-pulse"
          style={{
            height: '20px',
            animationDelay: `${index * 0.1}s`,
            animationDuration: '1s'
          }}
        />
      ))}
    </div>
  );
};

// Morphing Shape Loading
export const MorphingLoading: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`w-12 h-12 ${className}`}>
      <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-lg animate-morphing"></div>
    </div>
  );
};

// Progress Bar Loading
interface ProgressLoadingProps {
  progress: number;
  className?: string;
}

export const ProgressLoading: React.FC<ProgressLoadingProps> = ({ 
  progress, 
  className = '' 
}) => {
  return (
    <div className={`w-full bg-slate-200 rounded-full h-2 ${className}`}>
      <div 
        className="bg-gradient-to-r from-emerald-500 to-cyan-500 h-2 rounded-full transition-all duration-300 ease-out"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
};

// Skeleton Loading
interface SkeletonProps {
  width?: string;
  height?: string;
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  width = 'w-full', 
  height = 'h-4', 
  className = '' 
}) => {
  return (
    <div className={`${width} ${height} bg-slate-200 rounded animate-pulse ${className}`}></div>
  );
};

// Skeleton Card
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`p-6 bg-white rounded-xl border border-slate-200 ${className}`}>
      <div className="flex items-start space-x-4">
        <Skeleton width="w-12" height="h-12" className="rounded-xl" />
        <div className="flex-1 space-y-3">
          <Skeleton height="h-4" />
          <Skeleton height="h-3" width="w-3/4" />
          <Skeleton height="h-3" width="w-1/2" />
        </div>
      </div>
    </div>
  );
};
